import React, { useState } from "react";
import { Button } from "primereact/button";
import { api } from "./api";
import { Field, Select, Status, today, useData } from "./common";
import { Filters } from "./Ledger";
import { reportPeriodRange } from "./reportPeriod";
export function Matrix({ report }) {
  const cols = [
      ...new Map(report.rows.map((r) => [r.itemId + ":" + r.unit, r])).values(),
    ],
    groups = [...new Map(report.rows.map((r) => [r.group, r.label])).entries()],
    units = [...new Set(cols.map((c) => c.unit))];
  const cells = new Map(
    report.rows.map((r) => [
      JSON.stringify([r.group, r.itemId, r.unit]),
      r.quantity,
    ])
  );
  return (
    <>
      <p>{report.totalsNote}</p>
      <div
        className="sd-lines sd-table-scroll"
        role="region"
        aria-label="Tabla del reporte"
        tabIndex={0}
      >
        <table>
          <thead>
            <tr>
              <th>Destino / agrupación</th>
              {cols.map((c) => (
                <th key={c.itemId + c.unit}>
                  {c.code}
                  <small>
                    {c.itemName} · {c.unit}
                  </small>
                </th>
              ))}
              {units.map((u) => (
                <th key={u}>Total {u}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(([g, name]) => (
              <tr key={g}>
                <th>{name}</th>
                {cols.map((c) => (
                  <td key={c.itemId + c.unit}>
                    {cells.get(JSON.stringify([g, c.itemId, c.unit])) || 0}
                  </td>
                ))}
                {units.map((u) => (
                  <td key={u}>{report.rowTotals[g + ":" + u] || 0}</td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th>Total</th>
              {cols.map((c) => (
                <td key={c.itemId + c.unit}>
                  {report.columnTotals[c.itemId + ":" + c.unit] || 0}
                </td>
              ))}
              {units.map((u) => (
                <td key={u}>
                  {Number(
                    cols
                      .filter((c) => c.unit === u)
                      .reduce(
                        (sum, c) =>
                          sum + (report.columnTotals[c.itemId + ":" + u] || 0),
                        0
                      )
                      .toFixed(6)
                  )}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
      {!report.rows.length && <p>Sin movimientos para estos filtros.</p>}
    </>
  );
}
function ReportResults({ kind, query }) {
  const state = useData("reportes/" + kind, query);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState("");
  return (
    <>
      <div className="sd-toolbar">
        <Button
          label="Exportar Excel"
          loading={busy}
          disabled={state.loading || !!state.error}
          onClick={async () => {
            setBusy(true);
            setError("");
            try {
              await api(
                "reportes/" + kind,
                "GET",
                undefined,
                { ...query, formato: "xlsx" },
                true
              );
            } catch (e) {
              setError(e.message);
            } finally {
              setBusy(false);
            }
          }}
        />
      </div>
      <Status {...state} error={state.error || error} />
      {state.data && <Matrix report={state.data} />}
    </>
  );
}
export default function Reports() {
  const currentDay = today();
  const [kind, setKind] = useState("consolidado"),
    [filters, setFilters] = useState({}),
    [group, setGroup] = useState("destino"),
    [period, setPeriod] = useState("to-date"),
    [month, setMonth] = useState(currentDay.slice(0, 7)),
    [half, setHalf] = useState(currentDay.slice(8) <= "15" ? "1" : "2"),
    [from, setFrom] = useState(currentDay.slice(0, 7) + "-01"),
    [to, setTo] = useState(currentDay);
  let range, rangeError;
  try {
    range = reportPeriodRange(period, { month, half, from, to, currentDay });
  } catch (e) {
    rangeError = e.message;
  }
  const query = { ...filters, ...range, agrupar: group };
  const displayDate = (value) => value.split("-").reverse().join("/");
  return (
    <section>
      <h2>Reportes</h2>
      <div className="sd-grid">
        <Select
          title="Período"
          value={period}
          options={[
            { value: "monthly", label: "Mensual" },
            { value: "to-date", label: "Mes a la fecha" },
            { value: "fortnight", label: "Quincenal" },
            { value: "custom", label: "Rango personalizado" },
          ]}
          onChange={setPeriod}
        />
        {["monthly", "fortnight"].includes(period) && (
          <Field title="Mes">
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </Field>
        )}
        {period === "fortnight" && (
          <Select
            title="Quincena"
            value={half}
            options={[
              { value: "1", label: "1 al 15" },
              { value: "2", label: "16 al último día" },
            ]}
            onChange={setHalf}
          />
        )}
        {period === "custom" && (
          <>
            <Field title="Desde">
              <input
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field title="Hasta">
              <input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
          </>
        )}
        <Select
          title="Reporte"
          value={kind}
          options={[
            "consolidado",
            "devoluciones",
            "excepcionales",
            "desincorporaciones",
          ]}
          onChange={setKind}
        />
        <Select
          title="Agrupar por"
          value={group}
          options={["destino", "insumo", "operador", "dia"]}
          onChange={setGroup}
        />
      </div>
      {range && (
        <p>
          Período consultado:{" "}
          <strong>
            {displayDate(range.desde)} al {displayDate(range.hasta)}
          </strong>{" "}
          (ambos días incluidos).
        </p>
      )}
      <Status error={rangeError} />
      <details>
        <summary>Filtros combinables</summary>
        <Filters report includeDates={false} onApply={setFilters} />
      </details>
      {range && (
        <ReportResults
          key={kind + JSON.stringify(query)}
          kind={kind}
          query={query}
        />
      )}
    </section>
  );
}
